import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, useTheme, spacing } from '@fti/design-system';
import { useAccessToken, useAuth } from '../../auth/hooks';
import { extractQuotes, ExtractError } from '../../api/quoteExtractor';
import { UploadPanel } from './UploadPanel';
import { ResultsPanel } from './ResultsPanel';
import type { BrowserQuote, Session } from './types';
import { initialSession } from './types';

function quoteId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}

export const QuoteExtractorPage: React.FC = () => {
  const { theme } = useTheme();
  const auth = useAuth();
  const token = useAccessToken();
  const [session, setSession] = useState<Session>(initialSession);
  const abortRef = useRef<AbortController | null>(null);
  const lastSubmissionRef = useRef<{ text?: string; file?: File; titleHint?: string } | null>(
    null,
  );

  const hasUserWork = useMemo(
    () => session.quotes.some((q) => q.starred || q.editedText !== null),
    [session.quotes],
  );

  useEffect(() => {
    if (!hasUserWork) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUserWork]);

  const performExtraction = useCallback(
    async (input: { text?: string; file?: File; titleHint?: string }) => {
      lastSubmissionRef.current = input;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setSession({
        ...initialSession,
        status: input.file ? 'uploading' : 'processing',
      });

      try {
        const result = await extractQuotes(input, token, controller.signal);
        const augmented: BrowserQuote[] = result.quotes.map((q) => ({
          ...q,
          id: quoteId(),
          editedText: null,
          starred: false,
        }));
        setSession({
          status: 'ready',
          submission: {
            kind: input.file ? 'file' : 'text',
            title: result.title,
            wordCount: result.source_metadata.word_count,
          },
          quotes: augmented,
          noQuotesReason: result.no_quotes_reason,
          sourceMetadata: result.source_metadata,
          error: null,
        });
      } catch (err) {
        if (controller.signal.aborted) {
          setSession(initialSession);
          return;
        }
        const message =
          err instanceof ExtractError
            ? err.message
            : err instanceof Error
            ? err.message
            : 'An unexpected error occurred.';
        setSession({
          ...initialSession,
          status: 'error',
          error: message,
        });
        if (err instanceof ExtractError && err.kind === 'unauthorized') {
          void auth.signinSilent().catch(() => {
            void auth.signinRedirect();
          });
        }
      }
    },
    [token, auth],
  );

  const handleSubmit = (input: { text?: string; file?: File; titleHint?: string }) => {
    void performExtraction(input);
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  const handleRetry = () => {
    if (lastSubmissionRef.current) {
      void performExtraction(lastSubmissionRef.current);
    }
  };

  const handleToggleStar = useCallback((id: string) => {
    setSession((s) => ({
      ...s,
      quotes: s.quotes.map((q) => (q.id === id ? { ...q, starred: !q.starred } : q)),
    }));
  }, []);

  const handleSetEdited = useCallback((id: string, text: string) => {
    setSession((s) => ({
      ...s,
      quotes: s.quotes.map((q) =>
        q.id === id ? { ...q, editedText: text === q.text ? null : text } : q,
      ),
    }));
  }, []);

  const handleResetEdit = useCallback((id: string) => {
    setSession((s) => ({
      ...s,
      quotes: s.quotes.map((q) => (q.id === id ? { ...q, editedText: null } : q)),
    }));
  }, []);

  const busy = session.status === 'uploading' || session.status === 'processing';
  const title = session.submission?.title ?? 'Quotes';

  return (
    <Stack gap="xl" style={{ width: '100%' }}>
      {/* Hero */}
      <section
        style={{
          paddingTop: spacing.xl,
          paddingBottom: spacing.lg,
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: '2.5rem',
            lineHeight: 1.1,
            fontWeight: 700,
            letterSpacing: '-0.025em',
            background: `linear-gradient(135deg, ${theme.colors.primary[800]}, ${theme.colors.primary[500]})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Find the line that gets quoted.
        </h1>
        <p
          style={{
            margin: `${spacing.md} auto 0 auto`,
            maxWidth: '640px',
            fontSize: '1.05rem',
            lineHeight: 1.55,
            color: theme.colors.text.secondary,
          }}
        >
          Paste a speech, interview, press release, or transcript. Quote Extractor surfaces the
          5–10 statements a journalist is most likely to pull — ranked by clarity, resonance, and
          newsworthiness.
        </p>
      </section>

      <UploadPanel
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        busy={busy}
        disabled={!auth.isAuthenticated}
      />

      <ResultsPanel
        session={session}
        onToggleStar={handleToggleStar}
        onSetEdited={handleSetEdited}
        onResetEdit={handleResetEdit}
        onRetry={handleRetry}
        title={title}
      />
    </Stack>
  );
};
